import type { PuzzleStatus, Role } from "../api/schemas";

const RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2 };

/** What each role may do (contracts/admin-api.md "Auth and roles"). */
export type Permission =
  | "content.review"
  | "schedule.edit"
  | "player.moderate"
  | "reports.resolve"
  | "pro.grant"
  | "config.edit"
  | "team.moderate"
  | "team.disband"
  | "jobs.run"
  | "studio.edit"
  | "studio.killSwitch"
  | "ads.edit"
  | "ads.configure"
  | "notifications.send"
  | "banners.edit"
  | "support.reply"
  | "content.translate"
  | "media.upload"
  | "stories.edit"
  | "brain.edit"
  | "lume.edit"
  | "lume.chat"
  | "cups.edit"
  | "cups.confirm"
  | "paywall.edit"
  | "app.updates"
  | "payments.view"
  | "payments.configure";

const REQUIRED: Record<Permission, Role> = {
  "content.review": "editor",
  "schedule.edit": "editor",
  /** Reset an offensive handle (contracts/phase1.md "Admin additions"). */
  "player.moderate": "editor",
  /** Dismiss or action reports (contracts/phase2.md §12: editor or above). */
  "reports.resolve": "editor",
  /** Support Pro grants and their revocation (contracts/phase2.md §12: admin only). */
  "pro.grant": "admin",
  "config.edit": "admin",
  /** Reset an offensive team name (contracts/phase3.md: editor or above). */
  "team.moderate": "editor",
  /** Disband a team (admin only). */
  "team.disband": "admin",
  /** Run jobs such as the weekly teams close (admin only). */
  "jobs.run": "admin",
  /** Puzzle Studio mutations: jobs, cancel, review, re-verify, spec edits, scheduling (contracts/studio.md: editor+). */
  "studio.edit": "editor",
  /** The AI kill switch, the config key studio.aiEnabled (config is admin only). */
  "studio.killSwitch": "admin",
  /** Ads formats and placements (ads.formats, ads.placements; contracts/ads.md §5: editors change placements). */
  "ads.edit": "editor",
  /** The ads master switch, provider, test mode, unit ids, grace, countries and SSV (admin only). */
  "ads.configure": "admin",
  /** Send a notification to players (contracts/admin-api.md "Notifications": admin only). */
  "notifications.send": "admin",
  /** Create, edit, pause and delete banners (contracts/messaging.md §2: editor+). */
  "banners.edit": "editor",
  /** Reply to players, close and reopen support conversations (contracts/messaging.md §4: editor+). */
  "support.reply": "editor",
  /** One-click Claude translation of admin-written text (POST /translate: editor+). */
  "content.translate": "editor",
  /** Upload images for announcements and banners (POST /media: editor+). */
  "media.upload": "editor",
  /** Create, edit, publish, schedule, archive and delete Learn stories (contracts/learn.md §1: editor+). */
  "stories.edit": "editor",
  /** Create, edit and delete daily brain cards (contracts/learn.md §2: editor+). */
  "brain.edit": "editor",
  /** Lume's line library: add, edit, turn on/off and delete lines (contracts/lume.md §1: editor+). The push switches are config (admin). */
  "lume.edit": "editor",
  /** Ask Lume usage, recent turns and settings (contracts/lume.md §2: admin). */
  "lume.chat": "admin",
  /** Create and edit cups, cancel them, disqualify entries, work the prize queue (contracts/tournaments.md §9: editor+). */
  "cups.edit": "editor",
  /** Confirm a cup's results so money-value prizes become claimable (contracts/tournaments.md §6: admin). */
  "cups.confirm": "admin",
  /** Paywall experiments and the paywall.* settings (contracts/paywall.md §5: admin). */
  "paywall.edit": "admin",
  /** App versions, release notes and the rating prompt (contracts/app-updates.md: admin). */
  "app.updates": "admin",
  /** Monetisation → Web payments: the Stripe connection, packages, transactions and webhook events (contracts/web.md §5.7: editor+). */
  "payments.view": "editor",
  /** Stripe keys, the payments.stripe settings and packages (contracts/web.md §5.7: admin). */
  "payments.configure": "admin",
};

export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return RANK[role] >= RANK[REQUIRED[permission]];
}

/** PATCH /puzzles/:id transitions. `published` is only ever set by the server. */
export const STATUS_TRANSITIONS: Record<PuzzleStatus, PuzzleStatus[]> = {
  draft: ["approved", "rejected"],
  approved: ["draft", "rejected"],
  published: ["retired"],
  rejected: [],
  retired: [],
};
